import React, { useState, FC, useEffect, useMemo } from 'react'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { components } from 'react-select'
import { blockInvalidChar, blockInvalidCharWithDecimal, getApiErrorMessage, rsStyles } from 'src/marketConnect/helpers'

import { FormikState } from 'formik'
import { InitialValuesForCreateProduct } from 'src/interfaces/createProductInterface'
import './product.scss'
import { useAppDispatch } from 'src/redux/storeHooks'
import {
  createCollectionAsync,
  deleteCollectionAsync,
  getCollectionsAsync,
  updateCollectionAsync,
} from 'src/redux/actions/omsProductActions'
import { useInfiniteSelect } from 'src/hooks/useInfiniteSelect'
import { getPlatformParentFieldDataCategoriesAsync } from 'src/redux/actions/templateAndPricingActions'
import { CategorySelect } from './CategorySelectComponent'
import CategoryPickerV2, { CategoryNodeData } from './CategoryPickerV2'
import PlatformCategoryTreeSelect from './PlatformCategoryTreeSelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faGear, faPenToSquare, faPlus, faTrashCan, faTruck, faWarehouse } from '@fortawesome/free-solid-svg-icons'
import LocationModal from './LocationsModal'
import { getWarehouseListAsync } from 'src/redux/actions/omsPageActions'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import InventoryDetailsTable from './InventoryDetailsTable'
import { DIMENSION_UNIT_OPTIONS, WEIGHT_UNIT_OPTIONS } from 'src/utils/constants'

interface Option {
  value: string
  label: string
}
const TAGS_SESSION_KEY = 'product.tags.options'

type CollectionModalMode = 'manage' | 'create' | 'rename' | 'delete' | null

type SecondPhaseType = {
  values: InitialValuesForCreateProduct
  handleChange: (e: string | React.ChangeEvent<any>) => void
  setFormikState: (
    f:
      | FormikState<InitialValuesForCreateProduct>
      | ((prevState: FormikState<InitialValuesForCreateProduct>) => FormikState<InitialValuesForCreateProduct>),
    cb?: (() => void) | undefined
  ) => void
  collectionOptions?: any[]
  mode: 'create' | 'edit'
  editInventoryData?: any[]
  platformId?: number
}

/** Simple chip label (no edit/delete inside chips) */
const CollectionMultiValueLabel = (props: any) => {
  const opt = props.data as Option
  return (
    <components.MultiValueLabel {...props}>
      <span>{opt.label}</span>
    </components.MultiValueLabel>
  )
}

type CollectionOptionExtraProps = {
  onEditOption: (opt: Option) => void
  onDeleteOption: (opt: Option) => void
}

const CollectionOptionRow = (props: any) => {
  const opt = props.data as Option
  const { onEditOption, onDeleteOption } = props.selectProps as CollectionOptionExtraProps

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span>{opt.label}</span>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-icon btn-sm"
            title="Rename"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onEditOption(opt)
            }}
          > <FontAwesomeIcon icon={faPenToSquare} />
          </button>

          <button
            type="button"
            className="btn btn-icon btn-sm"
            title="Delete"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDeleteOption(opt)
            }}
          ><FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
    </components.Option>
  )
}

const SecondPhase: FC<SecondPhaseType> = ({ values, handleChange, setFormikState, mode, editInventoryData = [], platformId, }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  console.log('SecondPhase render', { values, mode })
  const [collectionModal, setCollectionModal] = useState<{
    mode: CollectionModalMode
    input: string
    active: Option | null
    loading: boolean
  }>({
    mode: null,
    input: '',
    active: null,
    loading: false,
  })

  const typeOptions = [{ value: 'GOODS', label: 'Goods' }, { value: 'SERVICES', label: 'Services' },]
  const [isPhysicalProduct, setIsPhysicalProduct] = useState<boolean>(true)
  const [showInventoryPopup, setShowInventoryPopup] = useState(false)
  const [inventoryRows, setInventoryRows] = useState<{ id: number; location?: string; location_name?: string; quantity: number; available_quantity?: number; reserve_quantity?: number; on_hand?: number }[]>([]);
  const [isCollectionMenuOpen, setIsCollectionMenuOpen] = useState(false);
  const [tagOptions, setTagOptions] = useState<Option[]>([])

  const [inventorySearch, setInventorySearch] = useState<string>('')
  const shouldShowSingleProductSections = !values?.isProductHasVariant
  const filteredInventoryRows = useMemo(() => {
    const q = inventorySearch.trim().toLowerCase()
    if (!q) return inventoryRows
    return inventoryRows.filter((r) => (r.location || '').toLowerCase().includes(q))
  }, [inventoryRows, inventorySearch])


  const syncEditModeCollectionLabels = async () => {
    if (mode !== 'edit') return
    if (!values.collection?.length) return

    const hasBlankLabels = values.collection.some((item: any) => !item?.label?.trim())
    if (!hasBlankLabels) return

    try {
      const searchIdValue = values.collection
        .map((item: any) => String(item?.value ?? '').trim())
        .filter(Boolean)
        .join(',')

      if (!searchIdValue) return

      const res: any = await dispatch(
        getCollectionsAsync({
          page: 1,
          limit: 50,
          id: searchIdValue,
        })
      ).unwrap()

      // FIX: support both shapes
      const list = res?.results?.data || res?.data || []

      const labelMap = new Map(
        list.map((item: any) => [
          String(item?.id), // collection master id
          item?.api_name,
        ])
      )

      const updatedCollections = (values.collection || []).map((item: any) => ({
        ...item,
        label: labelMap.get(String(item.value)) || item.label || '',
      }))

      setFieldValue('collection', updatedCollections)
    } catch (error) {
      console.error('Failed to sync edit mode collections', error)
    }
  }

  const buildInventoryRowsForEdit = (
    warehouses: any[],
    editInventoryData: any[]
  ): { id: number; location: string; quantity: number; reserve_quantity?: number; on_hand?: number }[] => {
    const firstVariant = editInventoryData?.[0];
    const locations: any[] = Array.isArray(firstVariant?.locations) ? firstVariant.locations : [];

    const locMap = new Map<number, any>(
      locations.map((loc: any) => [Number(loc.location_id), loc])
    );

    return (warehouses || []).map((w: any) => {
      const loc = locMap.get(Number(w.id));
      return {
        id: Number(w.id),
        location: String(w.api_name ?? ''),
        quantity: Number(loc?.available_quantity ?? 0),
        reserve_quantity: Number(loc?.reserve_quantity ?? 0),
        on_hand: Number(loc?.on_hand ?? 0),
      };
    });
  };

  useEffect(() => {
    const raw = sessionStorage.getItem(TAGS_SESSION_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) setTagOptions(parsed)
      } catch {
        // ignore
      }
    }
  }, [])

  // Persist tag options for the life of the tab
  useEffect(() => {
    sessionStorage.setItem(TAGS_SESSION_KEY, JSON.stringify(tagOptions))
  }, [tagOptions])

  //  Ensure selected tags are also present in dropdown options (edit mode + any time)
  useEffect(() => {
    const selected = (values.selectedTagValues || []) as Option[];
    if (!selected.length) return;

    setTagOptions((prev) => {
      const map = new Map<string, Option>();
      prev.forEach((o) => map.set(o.value.toLowerCase(), o));
      selected.forEach((o) => map.set(o.value.toLowerCase(), { value: o.value, label: o.label }));
      return Array.from(map.values());
    });
  }, [values.selectedTagValues]);

  const setFieldValue = (field: keyof InitialValuesForCreateProduct, value: any) => {
    setFormikState((prev) => ({
      ...prev, values: { ...prev.values, [field]: value },
    }))
  }

  useEffect(() => {
    if (mode === 'edit') {
      syncEditModeCollectionLabels()
    }
  }, [mode, values.collection?.length])

  useEffect(() => {
    if (!shouldShowSingleProductSections) return;

    const currentTracked = values?.varients_data?.[0]?.isTracked;

    if (currentTracked !== undefined) return;

    setFormikState((prev: any) => {
      const variants = Array.isArray(prev.values?.varients_data)
        ? prev.values.varients_data
        : [];

      const nextVariants =
        variants.length > 0
          ? variants.map((variant: any, index: number) =>
            index === 0
              ? { ...variant, isTracked: isPhysicalProduct }
              : variant
          )
          : [
            {
              price: { type: "PRICE", value: 0 },
              weight: { type: "WEIGHT", value: 0, unit: "kg" },
              quantity: 0,
              sku: "",
              barcode: "",
              ean: "",
              total_quantity: 0,
              isTracked: isPhysicalProduct,
            },
          ];

      return {
        ...prev,
        values: {
          ...prev.values,
          varients_data: nextVariants,
        },
      };
    });
  }, [isPhysicalProduct, shouldShowSingleProductSections, values?.varients_data?.[0]?.isTracked, setFormikState]);

  useEffect(() => {
    const tracked = values?.varients_data?.[0]?.isTracked;

    if (tracked === undefined) return;

    setIsPhysicalProduct(Boolean(tracked));
  }, [values?.varients_data?.[0]?.isTracked]);

  const addTagOptionIfMissing = (name: string) => {
    const label = name.trim()
    if (!label) return
    const value = label // or slugify(label) if you prefer
    setTagOptions((prev) => {
      const exists = prev.some((o) => o.value.toLowerCase() === value.toLowerCase())
      return exists ? prev : [...prev, { value, label }]
    })
  }

  const handleTagsChange = (items: Option[] | null) => {
    const next = items || []
    // make sure any selected tags also exist in the dropdown list
    next.forEach((t) => addTagOptionIfMissing(t.label))
    setFieldValue('selectedTagValues', next)
  }

  // ---- Infinite selects ----
  const collectionsSelect = useInfiniteSelect(async ({ page, search }) => {
    const res = await dispatch(getCollectionsAsync({ page, limit: 50, search })).unwrap()
    const list = res?.results?.data || res?.data || []
    return {
      options: list.map((item: any) => ({
        value: String(item?.id),
        label: item?.api_name,
      })),
      hasMore: Boolean(res?.next),
    }
  })

  const categoriesSelect = useInfiniteSelect(async ({ page, search }) => {
    const res = await dispatch(
      getPlatformParentFieldDataCategoriesAsync({ platform_Id: 52956, page, limit: 10, })
    ).unwrap()

    return {
      options: res.map((item: any) => ({ label: item.api_name, value: String(item.id), })),
      hasMore: res.length === 10,
    }
  })

  useEffect(() => {
    collectionsSelect.loadInitial()
    categoriesSelect.loadInitial()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // keep labels in sync for edit mode
  useEffect(() => {
    if (mode === 'edit') return
    if (!collectionsSelect.options?.length) return
    if (!values.collection?.length) return

    const labelMap = new Map(
      collectionsSelect.options.map((op: any) => [String(op.value), op.label])
    )

    const updated = (values.collection as any[]).map((c: any) => ({
      ...c,
      label: labelMap.get(String(c.value)) || c.label || `Collection ${c.value}`,
    }))

    setFieldValue('collection', updated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionsSelect.options, mode])

  // ---- Modal helpers ----
  const openModal = (m: Exclude<CollectionModalMode, null>, payload?: { name?: string; option?: Option }) => {
    setCollectionModal({
      mode: m, input: payload?.name ?? payload?.option?.label ?? '', active: payload?.option ?? null, loading: false,
    })
  }

  const closeModal = () => setCollectionModal({ mode: null, input: '', active: null, loading: false })

  const handleInventoryToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsPhysicalProduct(checked);

    setFormikState((prev: any) => ({
      ...prev,
      values: {
        ...prev.values,
        inventory: checked ? prev.values.inventory : [],
        varients_data: prev.values?.varients_data?.map((variant: any, index: number) =>
          index === 0
            ? { ...variant, isTracked: checked }
            : variant
        ),
      },
    }));
  };
  console.log(values?.varients_data[0]?.isTracked, "isTracked in main form state")

  const fetchAllWarehouses = async (search = "") => {
    let page = 1;
    let hasNext = true;
    const all: any[] = [];
    while (hasNext) {
      const res: any = await dispatch(
        getWarehouseListAsync({
          paramData: {
            page, limit: 50, search, sort_by: "id", sort_order: "asc", warehouse_id: null, isActive: true,
          },
        }));
      const payload = res?.payload;
      const rows = payload?.results?.data || payload?.data?.results?.data || payload?.data || payload?.results || [];
      all.push(...rows);
      hasNext = payload?.next !== null && payload?.next !== undefined;
      page += 1;
      if (!rows.length) break; // safety
    }
    return all;
  };

  useEffect(() => {
    const initInventory = async () => {
      if (!isPhysicalProduct) return;

      const warehouses = await fetchAllWarehouses("");

      let rows: { id: number; location: string; quantity: number }[] = [];

      if (mode === "edit" && editInventoryData?.length) {
        rows = buildInventoryRowsForEdit(warehouses, editInventoryData);
      } else if (values?.inventory?.length) {
        rows = values.inventory;
      } else {
        rows = warehouses.map((w: any) => ({
          id: w.id,
          location: w.api_name,
          quantity: 0,
        }));
      }

      setInventoryRows(rows);
      setFormikState((prev: any) => ({
        ...prev,
        values: {
          ...prev.values,
          inventory: rows,
        },
      }));
    };

    initInventory();
  }, [mode, isPhysicalProduct, editInventoryData]);

  const handleSaveInventoryLocations = (selectedLocations: { id: number; api_name: string }[]) => {
    const rows = selectedLocations.map((loc) => ({
      id: loc.id,
      location: loc.api_name,
      quantity: 0,
    }));
    setInventoryRows(rows);
    // store in formik too
    setFormikState((prev: any) => ({
      ...prev, values: {
        ...prev.values, inventory: rows,
      },
    }));
    setShowInventoryPopup(false);
  };

  // Handler function for checkbox change  
  const handleCheckboxChange = (e) => {
    setIsPhysicalProduct(!isPhysicalProduct)
  }

  const handleQuantityChange = (id: number, value: any) => {
    setInventoryRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, quantity: Number(value) } : row))
    );
    setFormikState((prev: any) => ({
      ...prev,
      values: {
        ...prev.values,
        inventory: (prev.values.inventory || []).map((row: any) =>
          row.id === id ? { ...row, quantity: Number(value) } : row
        ),
      },
    }));
  };

  // CREATE / RENAME / DELETE
  const submitModal = async () => {
    const { mode: mMode, input, active } = collectionModal
    if (!mMode || mMode === 'manage') return

    try {
      // CREATE
      if (mMode === 'create') {
        const name = input.trim()
        if (!name) {
          toast.error('Please fill collection name')
          return
        }

        setCollectionModal((s) => ({ ...s, loading: true }))

        // If this rejects, it will jump to catch and toast the backend message
        const res: any = await dispatch(createCollectionAsync({ api_name: name })).unwrap()

        // If your API sometimes returns {status, message} even on success, you can check it:
        const status = res?.status
        if (status && status !== 200 && status !== 201) {
          toast.error(res?.message || 'Failed to create collection')
          setCollectionModal((s) => ({ ...s, loading: false }))
          return
        }

        const created = res?.results?.data ?? res?.data ?? res
        const createdId = created?.id
        const createdName = created?.api_name ?? name

        if (!createdId) {
          toast.error('Collection created but no id returned')
          collectionsSelect.loadInitial()
          closeModal()
          return
        }

        const newOpt: Option = { value: String(createdId), label: String(createdName) }
        setFieldValue('collection', [...(values.collection || []), newOpt])

        collectionsSelect.loadInitial()
        closeModal()
        toast.success('Collection created')
        return
      }

      // RENAME
      if (mMode === 'rename' && active) {
        const name = input.trim()
        if (!name) {
          toast.error('Please fill collection name')
          return
        }

        setCollectionModal((s) => ({ ...s, loading: true }))

        const res: any = await dispatch(
          updateCollectionAsync({ id: Number(active.value), data: { api_name: name } })
        ).unwrap()

        const status = res?.status
        if (status && status !== 200 && status !== 201) {
          toast.error(res?.message || 'Failed to update collection')
          setCollectionModal((s) => ({ ...s, loading: false }))
          return
        }

        setFieldValue('collection',
          (values.collection || []).map((c: any) =>
            String(c.value) === String(active.value) ? { ...c, label: name } : c
          )
        )

        collectionsSelect.loadInitial()
        closeModal()
        toast.success('Collection updated')
        return
      }

      // DELETE
      if (mMode === 'delete' && active) {
        setCollectionModal((s) => ({ ...s, loading: true }))

        const res: any = await dispatch(deleteCollectionAsync({ id: Number(active.value) })).unwrap()

        const status = res?.status
        if (status && status !== 200 && status !== 201) {
          toast.error(res?.message || 'Failed to delete collection')
          setCollectionModal((s) => ({ ...s, loading: false }))
          return
        }

        setFieldValue('collection', (values.collection || []).filter((c: any) => String(c.value) !== String(active.value))
        )

        collectionsSelect.loadInitial()
        closeModal()
        toast.success(res?.message || 'Collection deleted')
        return
      }
    } catch (err: any) {
      console.error(err)
      toast.error(getApiErrorMessage(err))
      setCollectionModal((s) => ({ ...s, loading: false }))
    }
  }

  // ---- Styles: fixed height + internal scroll for selected chips ----
  const collectionSelectStyles = useMemo(() => {
    return {
      ...rsStyles,
      valueContainer: (base: any) => ({
        ...base,
        maxHeight: 92,
        overflowY: 'auto',
        alignItems: 'flex-start',
      }),
      control: (base: any, state: any) => ({
        ...(rsStyles as any).control ? (rsStyles as any).control(base, state) : base,
        minHeight: 44,
      }),
    }
  }, [])

  // Inject handlers into selectProps for Option row actions (TS-safe via cast)
  const optionRowExtras: CollectionOptionExtraProps = {
    onEditOption: (opt) => {
      setIsCollectionMenuOpen(false);
      openModal("rename", { option: opt });
    },
    onDeleteOption: (opt) => {
      setIsCollectionMenuOpen(false);
      openModal("delete", { option: opt });
    },
  };

  console.log("isPhysicalProduct", isPhysicalProduct)

  return (
    <div>
      <div className="d-flex flex-column gap-4 w-100">
        {/* Classification + Inventory */}
        <div className="row g-4 align-items-stretch">
          {/* LEFT: Classification */}
          <div className="col-12 col-lg-6 d-flex">
            <div className="card card-flush w-100 h-100">
              <div className="card-headerP d-flex justify-content-between align-items-center">
                <div className="card-titleP">
                  <h2>Classification</h2>
                </div>
                <a
                  href={`/manage-categories/${platformId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-link btn-sm p-0 m-0 text-primary fw-bold"
                >
                  Manage Categories
                </a>
              </div>

              {/* Category Product (Old - Commented out) */}
              {/*
              <div className="card-bodyP pt-0 mt-2">
                <label className="form-label d-block">Category</label>
                <div >
                  <CategorySelect
                    value={(values.category as any) || null}
                    onChange={(val) =>
                      setFormikState((prev) => ({
                        ...prev,
                        values: {
                          ...prev.values,
                          category: val
                            ? ({
                              ...val,
                              level: val.depth === 0 ? "parent" : "child",
                            } as any)
                            : null,
                        },
                      }))
                    }
                  />
                </div>
              </div>
              */}

              {/* ── V2 Category Picker (development preview) ── */}
              {/* <div className="card-bodyP pt-0 mt-2">
                <CategoryPickerV2
                  value={(values as any).__categoryV2 || null}
                  onChange={(val) =>
                    setFormikState((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        __categoryV2: val as any,
                      },
                    }))
                  }
                />
              </div> */}
              <div className="card-bodyP pt-0 mt-2">
                <PlatformCategoryTreeSelect
                  platformId={platformId}
                  value={(values as any).__categoryV2 || null}
                  onChange={(val) =>
                    setFormikState((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        __categoryV2: val
                          ? (val as any)
                          : {
                              id: null,
                              relationId: (prev.values as any).__categoryV2?.relationId ?? null,
                            },
                        category: val
                          ? {
                              value: String(val.id),
                              label: val.name,
                              level: val.depth === 0 ? "parent" : "child",
                              parentId: val.parentId ? String(val.parentId) : undefined,
                            }
                          : null,
                      },
                    }))
                  }
                />
              </div>

              <div className="card-bodyP pt-0">
                {/* TYPE */}
                <label className="form-label d-block required">Type</label>
                <Select
                  classNamePrefix="rs"
                  styles={rsStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  name="type"
                  options={typeOptions}
                  value={values.type ? typeOptions.find((op) => op.value === values.type) : null}
                  onChange={(selectedOption: any) =>
                    setFormikState((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        type: selectedOption?.value || '',
                      },
                    }))
                  }
                  placeholder="Select type..."
                />

                {/* VENDOR */}
                <label className="form-label d-block mt-3">Vendor</label>
                <input
                  type="text"
                  className="form-control"
                  value={values.vendor}
                  onChange={(e) => setFieldValue('vendor', e.target.value)}
                />

                {/* COLLECTION (Hidden)
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <label className="form-label d-block m-0">Collection</label>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm"
                      title="Create collection"
                      onClick={() => openModal('create', { name: '' })}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add
                    </button>

                    <button
                      type="button"
                      className="btn btn-icon btn-sm"
                      title="Manage selected collections"
                      onClick={() => openModal('manage')}
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </button>
                  </div>
                </div>

                <CreatableSelect
                  {...(optionRowExtras as any)}
                  isMulti
                  classNamePrefix="rs"
                  styles={collectionSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  maxMenuHeight={260}
                  options={collectionsSelect.options}
                  isLoading={collectionsSelect.loading}
                  onMenuScrollToBottom={collectionsSelect.loadMore}
                  onInputChange={collectionsSelect.onSearch}
                  value={values.collection || []}
                  closeMenuOnSelect={false}
                  placeholder="Select/Create collections..."
                  onChange={(selected: any) => setFieldValue('collection', selected || [])}
                  onCreateOption={(name) => openModal('create', { name })}
                  menuIsOpen={isCollectionMenuOpen}
                  onMenuOpen={() => setIsCollectionMenuOpen(true)}
                  onMenuClose={() => setIsCollectionMenuOpen(false)}
                  components={{
                    MultiValueLabel: CollectionMultiValueLabel,
                    Option: CollectionOptionRow,
                  }}
                />
                */}

                {/* TAGS */}
                <label className="form-label d-block mt-3">Tags</label>
                <CreatableSelect
                  isMulti
                  classNamePrefix="rs"
                  styles={rsStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={tagOptions}
                  value={values.selectedTagValues || []}
                  onChange={(items: any) => handleTagsChange(items)}
                  onCreateOption={(inputValue) => {
                    // 1) add it to dropdown options
                    addTagOptionIfMissing(inputValue)

                    // 2) also select it immediately
                    const label = inputValue.trim()
                    if (!label) return
                    const newOpt = { value: label, label }
                    setFieldValue('selectedTagValues', [...(values.selectedTagValues || []), newOpt])
                  }}
                  placeholder="Add tags..."
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                />

                {/* PRODUCT STATUS */}
                <label className="form-label d-block mt-3">Product Status</label>
                <Select
                  classNamePrefix="rs"
                  styles={rsStyles}
                  options={[
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'INACTIVE', label: 'Inactive' },
                    { value: 'DRAFT', label: 'Draft' },
                  ]}
                  value={{ 
                    value: values.selectedStatus?.toUpperCase() || 'DRAFT', 
                    label: values.selectedStatus ? (values.selectedStatus.charAt(0).toUpperCase() + values.selectedStatus.slice(1).toLowerCase()) : 'Draft' 
                  }}
                  onChange={(opt: any) => setFieldValue('selectedStatus', opt?.value || 'DRAFT')}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>

          {/* RIGHT: Inventory */}
          {shouldShowSingleProductSections && (
            <div className="col-12 col-lg-6 d-flex">
              <div className="card card-flush w-100 h-100">
                <div className="card-headerP d-flex justify-content-between align-items-center">
                  <div className="card-titleP">
                    <h2 className="mb-0">Inventory Details</h2>
                  </div>

                  {isPhysicalProduct && (
                    <div className="mx-3 flex-grow-1" style={{ maxWidth: '400px' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search inventory location..."
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="d-flex align-items-center gap-2">
                    <label className="form-check-label text-muted h4 mb-0" htmlFor="inventoryToggle" style={{ fontSize: '13px' }}>
                      Track Inventory
                    </label>

                    <div className="form-check form-switch m-0">
                      <input className="form-check-input h-20px w-40px" type="checkbox" id="inventoryToggle"
                        checked={isPhysicalProduct} onChange={handleInventoryToggle}
                      />
                    </div>
                  </div>
                </div>

                {isPhysicalProduct ? (
                  <div className="card-bodyP pt-0 mt-2">
                    <InventoryDetailsTable 
                      inventoryRows={filteredInventoryRows} 
                      handleQuantityChange={handleQuantityChange} 
                    />  
                  </div>
                ) : (
                  <div className="card-bodyP pt-0 mt-2 py-5 text-center">
                    <span className="text-muted fs-7">Track Inventory is turned off. Enable it to manage inventory details.</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pricing + Shipping + Dimensions */}
        <div className="row g-4 align-items-stretch">
          {/* LEFT: Pricing */}
          {shouldShowSingleProductSections && (
            <div className="col-12 col-lg-7 d-flex">
              <div className="card card-flush w-100">
                <div className="card-headerP">
                  <div className="card-titleP">
                    <h2>Pricing</h2>
                  </div>
                </div>

                <div className="card-bodyP pt-0 mt-2">
                  {/* Top Row: Sale Price & Cost Price */}
                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6 fv-row">
                      <label className="required form-label">Sale Price</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        onKeyDown={blockInvalidCharWithDecimal}
                        name="salePrice"
                        className="form-control"
                        placeholder="0"
                        value={values.salePrice}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12 col-md-6 fv-row">
                      <label className="form-label">Cost Price</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        name="costPrice"
                        className="form-control"
                        placeholder="0"
                        value={values.costPrice}
                        onChange={handleChange}
                        onKeyDown={blockInvalidCharWithDecimal}
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {/* Bottom Row: Compare Price */}
                  <div className="row g-3">
                    <div className="col-12 col-md-6 fv-row">
                      <label className="form-label">Compare Price</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        name="comparePrice"
                        className="form-control"
                        placeholder="0"
                        value={values.comparePrice}
                        onChange={handleChange}
                        onKeyDown={blockInvalidCharWithDecimal}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RIGHT: Shipping & Dimensions */}
          <div className={`col-12 ${shouldShowSingleProductSections ? 'col-lg-5' : ''} d-flex`}>
              <div className="card card-flush w-100">
                <div className="card-headerP">
                  <div className="card-titleP">
                    <h2>Shipping & Dimensions</h2>
                  </div>
                </div>

                <div className="card-bodyP pt-0 mt-2">
                  {/* Weight */}
                  <div className="row g-3 align-items-end mb-4">
                    <div className="col-12 col-md-8 fv-row">
                      <label className="form-label">Product weight</label>
                      <div className="d-flex gap-3">
                        <input
                          type="number"
                          name="weight"
                          className="form-control"
                          placeholder="0.0"
                          value={values.weight}
                          onChange={handleChange}
                          onKeyDown={blockInvalidCharWithDecimal}
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                        />

                        <div className="unitSelectWrap" style={{ maxWidth: '90px', minWidth: '90px' }}>
                          <select
                            name="weightUnit"
                            className="form-select unitSelect"
                            value={values.weightUnit}
                            onChange={handleChange}
                            style={{ textTransform: 'uppercase' }}
                          >
                            {WEIGHT_UNIT_OPTIONS.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label.toUpperCase()}
                              </option>
                            ))}
                          </select>
                          <span className="unitSelectIcon">
                            <FontAwesomeIcon icon={faChevronDown} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="row g-3">
                    <div className="col-12 col-md-3">
                      <label className="form-label">Length</label>
                      <input
                        type="number"
                        name="length"
                        className="form-control"
                        placeholder="0.0"
                        value={(values as any).length ?? ''}
                        onChange={handleChange}
                        onKeyDown={blockInvalidCharWithDecimal}
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label">Width</label>
                      <input
                        type="number"
                        name="width"
                        className="form-control"
                        placeholder="0.0"
                        value={(values as any).width ?? ''}
                        onChange={handleChange}
                        onKeyDown={blockInvalidCharWithDecimal}
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label">Height</label>
                      <input
                        type="number"
                        name="height"
                        className="form-control"
                        placeholder="0.0"
                        value={(values as any).height ?? ''}
                        onChange={handleChange}
                        onKeyDown={blockInvalidCharWithDecimal}
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label">Unit</label>
                      <div className="unitSelectWrap">
                        <select
                          name="dimensionUnit"
                          className="form-select unitSelect"
                          value={values.dimensionUnit ?? 'CM'}
                          onChange={handleChange}
                          style={{ textTransform: 'uppercase' }}
                        >
                          {DIMENSION_UNIT_OPTIONS.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label.toUpperCase()}
                            </option>
                          ))}
                        </select>
                        <span className="unitSelectIcon">
                          <FontAwesomeIcon icon={faChevronDown} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

      </div>

      {/* SINGLE MODAL (Manage + Create + Rename + Delete) */}
      {collectionModal.mode && (
        <div className="image-modal-backdrop" onClick={closeModal}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 350, zIndex: 9999 }}>
            <button className="close-btn" onClick={closeModal}>
              ×
            </button>

            {/* MANAGE VIEW */}
            {collectionModal.mode === 'manage' && (
              <>
                <h3 style={{ marginBottom: 14 }}>Manage Collections</h3>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button className="btn btn-primary" onClick={() => openModal('create', { name: '' })}>
                    + Create
                  </button>
                </div>

                {(values.collection || []).length ? (
                  <div style={{ border: '1px solid #dbe3f0', borderRadius: 10, overflow: 'hidden' }}>
                    {(values.collection || []).map((c: any) => (
                      <div
                        key={String(c.value)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          borderBottom: '1px solid #e7eef8',
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{c.label}</div>

                        <div style={{ display: 'flex', gap: 10 }}>
                          <button className="btn btn-light" onClick={() => openModal('rename', { option: c })}>
                            Update
                          </button>
                          <button className="btn btn-danger" onClick={() => openModal('delete', { option: c })}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">No collections selected.</div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button className="btn btn-light" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </>
            )}

            {/* CREATE / RENAME / DELETE VIEW */}
            {collectionModal.mode !== 'manage' && (
              <>
                <h3 style={{ marginBottom: 14 }}>
                  {collectionModal.mode === 'create' && 'Create Collection'}
                  {collectionModal.mode === 'rename' && 'Update Collection'}
                  {collectionModal.mode === 'delete' && 'Delete Collection'}
                </h3>

                {collectionModal.mode === 'delete' ? (
                  <p className="text-muted" style={{ marginTop: 8 }}>
                    Are you sure you want to delete <b>{collectionModal.active?.label}</b>?
                  </p>
                ) : (
                  <>
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      value={collectionModal.input}
                      onChange={(e) => setCollectionModal((s) => ({ ...s, input: e.target.value }))}
                      autoFocus
                    />
                  </>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }} >
                  <button type="button" className="btn btn-light" onClick={() => openModal('manage')} disabled={collectionModal.loading}>
                    Back
                  </button>

                  <button
                    type="button"
                    className={`btn ${collectionModal.mode === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                    onClick={submitModal}
                    disabled={collectionModal.loading}
                  >
                    {collectionModal.loading ? 'Saving...' : collectionModal.mode === 'delete' ? 'Delete' : collectionModal.mode === 'create' ? 'Create' : 'Update'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showInventoryPopup && (
        <LocationModal
          show={showInventoryPopup}
          onClose={() => setShowInventoryPopup(false)}
          onSave={handleSaveInventoryLocations}
        />
      )}
    </div>
  )
}

export default SecondPhase